import { Injectable, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AccountCompanyService } from './account-company.service';
import { AccountingCompanyContextService } from './accounting-company-context.service';
import { MasterDataCacheService } from './master-data-cache.service';
import { AccountCompanyWiseConfigService } from './account-company-wise-config.service';
import { AccountCompanyDto } from '../models/dtos/account-company.dto';

export interface CurrentCompanySnapshot {
  companyId: string;
  companyName: string;
  mailingName?: string | null;
}

export type CompanySwitchRedirect = 'accounting' | 'dashboard' | 'none';

/**
 * Solvix company context for Accounts.
 * Uses Solvix session auth only — no separate Auth/select-company exchange.
 */
@Injectable({ providedIn: 'root' })
export class CurrentCompanyService {
  private readonly accountCompanyService = inject(AccountCompanyService);
  private readonly companyContext = inject(AccountingCompanyContextService);
  private readonly masterDataCache = inject(MasterDataCacheService);
  private readonly companyWiseConfig = inject(AccountCompanyWiseConfigService);
  private readonly router = inject(Router);

  private readonly currentCompanySubject =
    new BehaviorSubject<CurrentCompanySnapshot | null>(null);

  /** Active company for header UI and subscribers. */
  readonly currentCompany$ = this.currentCompanySubject.asObservable();

  constructor() {
    effect(() => {
      const dto = this.companyContext.company();
      if (!dto?.accountCompanyId) {
        return;
      }

      const next = this.toSnapshot(dto);
      const current = this.currentCompanySubject.value;
      if (
        current?.companyId?.toLowerCase() === next.companyId.toLowerCase() &&
        current.companyName === next.companyName
      ) {
        return;
      }

      this.currentCompanySubject.next(next);
    });
  }

  get snapshot(): CurrentCompanySnapshot | null {
    return this.currentCompanySubject.value;
  }

  /** Clears in-memory company state without touching Solvix auth storage. */
  clearSession(): void {
    this.currentCompanySubject.next(null);
  }

  /** Sync BehaviorSubject from loaded accounting company context. */
  syncFromContext(): void {
    const dto = this.companyContext.company();
    if (!dto?.accountCompanyId) {
      return;
    }

    this.currentCompanySubject.next(this.toSnapshot(dto));
  }

  /**
   * Opens company context on first Accounts entry using the active/default company id.
   */
  ensureAccountingCompanyOpen(): Observable<boolean> {
    this.accountCompanyService.markCompanyOpen();
    const companyId = this.accountCompanyService.getActiveCompanyId();
    return this.applyCompanyById(companyId, false);
  }

  /**
   * Validates and applies a company switch (F3). Clears caches and returns to gateway.
   */
  switchCompany(
    companyId: string,
    redirectTo: CompanySwitchRedirect = 'accounting',
  ): Observable<boolean> {
    const normalized = companyId.trim().toLowerCase();
    const current = this.currentCompanySubject.value?.companyId?.trim().toLowerCase();
    if (current && current === normalized) {
      return of(true);
    }

    return this.applyCompanyById(companyId, true).pipe(
      tap((success) => {
        if (!success || redirectTo === 'none') return;
        void this.router.navigate([redirectTo === 'dashboard' ? '/dashboard' : '/accounting']);
      }),
    );
  }

  /**
   * Applies company selection without forcing gateway navigation.
   */
  applyLoginSelection(companyId: string): Observable<boolean> {
    const normalized = companyId.trim().toLowerCase();
    const current = this.currentCompanySubject.value?.companyId?.trim().toLowerCase();
    if (current && current === normalized) {
      return of(true);
    }

    return this.applyCompanyById(companyId, true);
  }

  private applyCompanyById(
    companyId: string,
    clearCaches: boolean,
  ): Observable<boolean> {
    const id = companyId.trim();
    if (!id) {
      return of(false);
    }

    if (clearCaches) {
      this.refreshCachedData();
    }

    this.accountCompanyService.setActiveCompanyId(id);
    this.accountCompanyService.markCompanyOpen();

    return this.accountCompanyService.getCompanyById(id).pipe(
      map((loadRes) => {
        if (loadRes.success && loadRes.data) {
          this.companyContext.switchToCompany(loadRes.data);
          this.currentCompanySubject.next(this.toSnapshot(loadRes.data));
          this.syncFromContext();
          if (clearCaches) {
            this.accountCompanyService.recordRecentLoginCompany(id).subscribe({
              error: () => undefined,
            });
          }
          return true;
        }
        return false;
      }),
      catchError(() => of(false)),
    );
  }

  private refreshCachedData(): void {
    this.masterDataCache.clearAll();
    this.companyWiseConfig.clearCache();
  }

  private toSnapshot(dto: AccountCompanyDto): CurrentCompanySnapshot {
    return {
      companyId: dto.accountCompanyId ?? '',
      companyName: dto.companyName?.trim() || 'Company',
      mailingName: dto.mailingName,
    };
  }
}
